"use client";

import React, { Suspense, lazy } from "react";

import HeroSectionFE from "@/app/frontend/components/HeroSectionFE";
import {
    ShieldCheck, Users, Cloud, Palette, DeviceMobile,
    ChartBar, Code, Flask, ChatCenteredText,
    HardDrives, MegaphoneSimple, ShoppingBag,
    Rocket, DeviceTablet, CheckCircle, MagnifyingGlass, ArrowsIn, Buildings
} from "@/app/components/Icons/Phosphoricons";

// // ✅ Lazy-load components for performance
const HighlightsFE = lazy(() => import("@/app/frontend/components/HighlightsFE"));
const WebsiteOptimization = lazy(() => import("@/app/frontend/components/WebsiteOptimization"));
const AccessibilityCompliance = lazy(() => import("@/app/frontend/components/AccessibilityCompliance"));
const EnterpriseHosting = lazy(() => import("@/app/frontend/components/EnterpriseHosting"));
const WebSecurity = lazy(() => import("@/app/frontend/components/WebSecurity"));
const EcommerceSolutions = lazy(() => import("@/app/frontend/components/EcommerceSolutions"));
const ContentFE = lazy(() => import("@/app/frontend/components/ContentFE"));
const AllInOnePlatform = lazy(() => import("@/app/components/AllInOnePlatform"));
const SmartFormsAIIntegration = lazy(() => import("@/app/frontend/components/SmartFormsAIIntegration"));
const PricingWebsites = lazy(() => import("@/app/pricing/components/PricingWebsites"));
// import HighlightsFE from "@/app/frontend/components/HighlightsFE";
// import WebsiteOptimization from "@/app/frontend/components/WebsiteOptimization";
// import AccessibilityCompliance from "@/app/frontend/components/AccessibilityCompliance";
// import EnterpriseHosting from "@/app/frontend/components/EnterpriseHosting";
// import WebSecurity from "@/app/frontend/components/WebSecurity";
// import ECommerceSolutions from "@/app/frontend/components/ECommerceSolutions";
// import ContentFE from "@/app/frontend/components/ContentFE";
// import AllInOnePlatform from "@/app/components/AllInOnePlatform";
// import SmartFormsAIIntegration from "@/app/frontend/components/SmartFormsAIIntegration";
// import PricingWebsites from "@/app/pricing/components/PricingWebsites";
// const HighlightsFE = dynamic(() => import("@/app/frontend/components/HighlightsFE"), { ssr: false });
// const WebsiteOptimization = dynamic(() => import("@/app/frontend/components/WebsiteOptimization"), { ssr: false });
// const AccessibilityCompliance = dynamic(() => import("@/app/frontend/components/AccessibilityCompliance"), { ssr: false });
// const EnterpriseHosting = dynamic(() => import("@/app/frontend/components/EnterpriseHosting"), { ssr: false });
// const WebSecurity = dynamic(() => import("@/app/frontend/components/WebSecurity"), { ssr: false });
// const ECommerceSolutions = dynamic(() => import("@/app/frontend/components/ECommerceSolutions"), { ssr: false });
// const ContentFE = dynamic(() => import("@/app/frontend/components/ContentFE"), { ssr: false });
// const AllInOnePlatform = dynamic(() => import("@/app/components/AllInOnePlatform"), { ssr: false });
// const SmartFormsAIIntegration = dynamic(() => import("@/app/frontend/components/SmartFormsAIIntegration"), { ssr: false });
// const PricingWebsites = dynamic(() => import("@/app/pricing/components/PricingWebsites"), { ssr: false });
//
const features = [
    // 🔹 UI & Design
    {
        name: "Responsive Web Design",
        icon: DeviceMobile,
        description: "Optimized websites that adapt seamlessly to all devices for the best user experience.",
    },
    {
        name: "Custom Designs",
        icon: Palette,
        description: "Unique, brand-focused UI/UX designs built with Figma, Adobe XD, or Sketch.",
    },
    {
        name: "Dark Mode & Theming",
        icon: Palette,
        description: "Supports light/dark mode and dynamic theming for better user experience.",
    },

    // 🔹 Performance & SEO
    {
        name: "Server Side Rendering (SSR) & Static Generation (SSG)",
        icon: Cloud,
        description: "Enhance SEO and page speed using Next.js SSR and SSG techniques.",
    },
    {
        name: "Edge Network & CDN",
        icon: Cloud,
        description: "Deploy to an edge network for ultra-low latency and global content delivery.",
    },
    {
        name: "Code Splitting & Lazy Loading",
        icon: ArrowsIn,
        description: "Optimized bundle sizes and faster page loads through intelligent code splitting.",
    },

    // 🔹 Security & Compliance
    {
        name: "Frontend Security Best Practices",
        icon: ShieldCheck,
        description: "Prevents XSS, CSRF, and other vulnerabilities with secure coding techniques.",
    },
    {
        name: "DDoS & Bot Protection",
        icon: ShieldCheck,
        description: "Enterprise-grade protection against malicious attacks and bot traffic.",
    },
    {
        name: "GDPR & CCPA Compliance",
        icon: CheckCircle,
        description: "Built-in data privacy features for GDPR and CCPA compliance.",
    },
    {
        name: "ADA & WCAG 2.1 Compliance",
        icon: Users,
        description: "Full accessibility compliance, including ARIA labels and screen reader optimization.",
    },

    // 🔹 AI & Automation
    {
        name: "AI-Powered Personalization",
        icon: Flask,
        description: "Deliver dynamic, tailored content based on real-time user behavior.",
    },
    {
        name: "AI Agents & Chatbots",
        icon: ChatCenteredText,
        description: "Automated customer interactions and support powered by AI assistants.",
    },

    // 🔹 API & Integrations
    {
        name: "GraphQL & REST API Support",
        icon: Code,
        description: "Efficient data fetching using GraphQL, REST APIs, and WebSockets.",
    },
    {
        name: "Headless CMS Integration",
        icon: HardDrives,
        description: "Seamless integration with Contentful, Strapi, and Sanity for flexible content management.",
    },

    // 🔹 E-Commerce & Business
    {
        name: "E-Commerce Solutions",
        icon: ShoppingBag,
        description: "Scalable, high-performance online stores with secure payment gateways.",
    },
    {
        name: "Marketing Automation",
        icon: MegaphoneSimple,
        description: "Automate email campaigns, social media scheduling, and customer outreach.",
    },

    // 🔹 Scalability & Enterprise Features
    {
        name: "Enterprise SaaS Integration",
        icon: Buildings,
        description: "Connect with CRMs, ERPs, and enterprise software for a seamless workflow.",
    },
    {
        name: "Scalable Infrastructure",
        icon: Rocket,
        description: "Cloud-native architecture designed for business growth.",
    },
    {
        name: "Progressive Web Apps (PWA)",
        icon: DeviceTablet,
        description: "Offline functionality, push notifications, and an app-like experience.",
    },

    // 🔹 Monitoring & Support
    {
        name: "Real-Time Monitoring & Metrics",
        icon: MagnifyingGlass,
        description: "Track site performance, uptime, and security threats with advanced analytics.",
    },
    {
        name: "Bug-Free & Optimized Performance",
        icon: ChartBar,
        description: "Maintained in-house for seamless integration and lightning-fast load times.",
    },
    {
        name: "Priority Bug Fixes & Updates",
        icon: MagnifyingGlass,
        description: "Receive continuous improvements, security patches, and performance optimizations.",
    },
];



export default function Page() {
    return (
        <main>
            {/* ✅ Hero Section loads immediately */}
            <HeroSectionFE />

            {/* ✅ Lazy-loaded sections with Suspense fallback */}
            <Suspense fallback={<div className="text-center text-gray-500 py-10">Loading Highlights...</div>}>
                <HighlightsFE />
            </Suspense>

            <Suspense fallback={<div className="text-center text-gray-500 py-10">Loading Website Optimization...</div>}>
                <WebsiteOptimization />
            </Suspense>

            <Suspense fallback={<div className="text-center text-gray-500 py-10">Loading Accessibility Compliance...</div>}>
                <AccessibilityCompliance />
            </Suspense>

            <Suspense fallback={<div className="text-center text-gray-500 py-10">Loading Enterprise Hosting...</div>}>
                <EnterpriseHosting />
            </Suspense>

            <Suspense fallback={<div className="text-center text-gray-500 py-10">Loading Web Security...</div>}>
                <WebSecurity />
            </Suspense>

            <Suspense fallback={<div className="text-center text-gray-500 py-10">Loading E-Commerce Solutions...</div>}>
                <EcommerceSolutions />
            </Suspense>

            <Suspense fallback={<div className="text-center text-gray-500 py-10">Loading Content...</div>}>
                <ContentFE />
            </Suspense>

            <Suspense fallback={<div className="text-center text-gray-500 py-10">Loading Smart Forms & AI Integration...</div>}>
                <SmartFormsAIIntegration />
            </Suspense>

            <Suspense fallback={<div className="text-center text-gray-500 py-10">Loading All-In-One Platform...</div>}>
                <AllInOnePlatform
                    title="Custom Software Solutions"
                    subtitle="Transform your business with custom software solutions that scale. Our full-stack expertise delivers everything from user interfaces to complex backend systems, all tailored to your specific needs."
                    features={features}
                />
            </Suspense>

            <Suspense fallback={<div className="text-center text-gray-500 py-10">Loading Pricing...</div>}>
                <PricingWebsites />
            </Suspense>
        </main>
    );
}
